import { useQuery } from '@tanstack/react-query';

import { endpoints } from '@/react-query/endpoints';

import { HUBSPOT_FORM_ID, HUBSPOT_PORTAL_ID } from '../constants';

interface HubSpotFieldOption {
  label: string;
  value: string;
}

interface HubSpotFormOptions {
  productRequest: HubSpotFieldOption[];
  primaryPms: HubSpotFieldOption[];
  imagingPlatform: HubSpotFieldOption[];
}

const FIELD_MAP: Record<string, keyof HubSpotFormOptions> = {
  product_request_qualifier_form: 'productRequest',
  primary_pms: 'primaryPms',
  imaging_platform: 'imagingPlatform',
};

let jsonpCounter = 0;

const fetchFormOptions = (): Promise<HubSpotFormOptions> => {
  return new Promise((resolve, reject) => {
    const callbackName = `__hsForm_${++jsonpCounter}`;
    const url = `${endpoints.hubspot.formDefinition(HUBSPOT_PORTAL_ID, HUBSPOT_FORM_ID)}?callback=${callbackName}`;

    const script = document.createElement('script');
    script.src = url;

    const cleanup = () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
      script.remove();
    };

    (window as unknown as Record<string, unknown>)[callbackName] = (data: {
      form: {
        formFieldGroups: Array<{
          fields: Array<{
            name: string;
            options?: Array<{ label: string; value: string }>;
          }>;
        }>;
      };
    }) => {
      cleanup();

      const result: HubSpotFormOptions = {
        productRequest: [],
        primaryPms: [],
        imagingPlatform: [],
      };

      for (const group of data.form.formFieldGroups) {
        for (const field of group.fields) {
          const key = FIELD_MAP[field.name];
          if (key && field.options?.length) {
            result[key] = field.options.map((opt) => ({
              label: opt.label,
              value: opt.value,
            }));
          }
        }
      }

      resolve(result);
    };

    script.onerror = () => {
      cleanup();
      reject(new Error('Failed to load HubSpot form definition'));
    };

    document.head.appendChild(script);
  });
};

export const useHubSpotFormOptions = () => {
  return useQuery({
    queryKey: ['hubspot-form-options'],
    queryFn: fetchFormOptions,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export type { HubSpotFieldOption, HubSpotFormOptions };
